import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { resolveAdminCrossCurricularCatalog } from '../../services/adminCrossCurricularService';
import { loadAdminCrossCurricularCatalogCache } from '../../utils/adminCrossCurricularCatalogCache';
import type {
  AdminCrossCurricularContent,
  AdminCrossCurricularTopic,
} from '../../types/adminCrossCurricular';

export type CrossCurricularCatalogPick = {
  topicName: string;
  body: string;
};

type NavPane = 'topics' | 'contents';

type Props = {
  open: boolean;
  onClose: () => void;
  onApply: (pick: CrossCurricularCatalogPick) => void;
};

const CrossCurricularCatalogPickerModal: React.FC<Props> = ({ open, onClose, onApply }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [topics, setTopics] = useState<AdminCrossCurricularTopic[]>([]);
  const [contents, setContents] = useState<AdminCrossCurricularContent[]>([]);
  const [selectedTopicId, setSelectedTopicId] = useState<string | null>(null);
  const [selectedContentId, setSelectedContentId] = useState<string | null>(null);
  const [contentFocusId, setContentFocusId] = useState<string | null>(null);
  const [navPane, setNavPane] = useState<NavPane>('topics');

  const dialogRef = useRef<HTMLDivElement>(null);
  const topicBtnRefs = useRef<Map<string, HTMLButtonElement>>(new Map());
  const contentBtnRefs = useRef<Map<string, HTMLButtonElement>>(new Map());
  const applyBtnRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setError(null);
    setSelectedTopicId(null);
    setSelectedContentId(null);
    setContentFocusId(null);
    setNavPane('topics');

    const cached = loadAdminCrossCurricularCatalogCache();
    if (cached.found) {
      setTopics(cached.catalog.topics);
      setContents(cached.catalog.contents);
      setSelectedTopicId(cached.catalog.topics[0]?.id ?? null);
      setLoading(false);
      return;
    }

    setLoading(true);
    void resolveAdminCrossCurricularCatalog()
      .then((catalog) => {
        if (cancelled) return;
        setTopics(catalog.topics);
        setContents(catalog.contents);
        setSelectedTopicId(catalog.topics[0]?.id ?? null);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : '범교과 예시문을 불러오지 못했습니다.');
        setTopics([]);
        setContents([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  const contentsForTopic = useMemo(() => {
    if (!selectedTopicId) return [] as AdminCrossCurricularContent[];
    return contents
      .filter((c) => c.topic_id === selectedTopicId)
      .slice()
      .sort((a, b) => a.sort_order - b.sort_order || a.created_at.localeCompare(b.created_at));
  }, [contents, selectedTopicId]);

  const selectedTopic = useMemo(
    () => topics.find((t) => t.id === selectedTopicId) ?? null,
    [selectedTopicId, topics]
  );

  const selectedContent = useMemo(
    () => contentsForTopic.find((c) => c.id === selectedContentId) ?? null,
    [contentsForTopic, selectedContentId]
  );

  const canApply = Boolean(selectedTopic && selectedContent && selectedContent.body.trim());

  const focusTopicButton = useCallback((topicId: string | null) => {
    if (!topicId) return;
    window.requestAnimationFrame(() => {
      topicBtnRefs.current.get(topicId)?.focus();
      topicBtnRefs.current.get(topicId)?.scrollIntoView({ block: 'nearest' });
    });
  }, []);

  const focusContentButton = useCallback((contentId: string | null) => {
    if (!contentId) return;
    window.requestAnimationFrame(() => {
      contentBtnRefs.current.get(contentId)?.focus();
      contentBtnRefs.current.get(contentId)?.scrollIntoView({ block: 'nearest' });
    });
  }, []);

  useEffect(() => {
    if (!open || loading || !topics.length) return;
    if (navPane === 'topics' && selectedTopicId) {
      focusTopicButton(selectedTopicId);
    }
  }, [open, loading, topics.length, navPane, selectedTopicId, focusTopicButton]);

  useEffect(() => {
    if (!selectedTopicId) {
      setContentFocusId(null);
      return;
    }
    setContentFocusId((prev) => {
      if (prev && contentsForTopic.some((c) => c.id === prev)) return prev;
      if (selectedContentId && contentsForTopic.some((c) => c.id === selectedContentId)) {
        return selectedContentId;
      }
      return contentsForTopic[0]?.id ?? null;
    });
  }, [selectedTopicId, contentsForTopic, selectedContentId]);

  const selectTopicByIndex = useCallback(
    (nextIndex: number) => {
      if (!topics.length) return;
      const idx = Math.max(0, Math.min(topics.length - 1, nextIndex));
      const topic = topics[idx];
      if (!topic) return;
      setSelectedTopicId(topic.id);
      setSelectedContentId(null);
      setNavPane('topics');
      focusTopicButton(topic.id);
    },
    [focusTopicButton, topics]
  );

  const moveContentFocus = useCallback(
    (delta: number) => {
      if (!contentsForTopic.length) return;
      const currentIdx = Math.max(
        0,
        contentsForTopic.findIndex((c) => c.id === contentFocusId)
      );
      const nextIdx = Math.max(0, Math.min(contentsForTopic.length - 1, currentIdx + delta));
      const next = contentsForTopic[nextIdx];
      if (!next) return;
      setContentFocusId(next.id);
      setNavPane('contents');
      focusContentButton(next.id);
    },
    [contentFocusId, contentsForTopic, focusContentButton]
  );

  const enterContentsPane = useCallback(() => {
    if (!contentsForTopic.length) return;
    const targetId =
      (contentFocusId && contentsForTopic.some((c) => c.id === contentFocusId)
        ? contentFocusId
        : null) ??
      selectedContentId ??
      contentsForTopic[0]?.id ??
      null;
    if (!targetId) return;
    setContentFocusId(targetId);
    setNavPane('contents');
    focusContentButton(targetId);
  }, [contentFocusId, contentsForTopic, focusContentButton, selectedContentId]);

  const handleApply = useCallback(() => {
    if (!selectedTopic || !selectedContent) return;
    const body = selectedContent.body.trim();
    if (!body) return;
    onApply({
      topicName: selectedTopic.name,
      body,
    });
  }, [onApply, selectedContent, selectedTopic]);

  const selectContentAndFocusApply = useCallback((contentId: string) => {
    const row = contentsForTopic.find((c) => c.id === contentId);
    if (!row || !row.body.trim()) return;
    setSelectedContentId(contentId);
    setContentFocusId(contentId);
    setNavPane('contents');
    // 선택 반영으로 버튼이 활성화된 뒤 포커스 이동
    window.requestAnimationFrame(() => {
      window.setTimeout(() => {
        applyBtnRef.current?.focus();
      }, 0);
    });
  }, [contentsForTopic]);

  const handleDialogKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
      return;
    }

    const target = e.target as HTMLElement | null;
    const onApplyBtn = target?.closest?.('.cp-cross-curricular-catalog-picker-apply');
    const onCancelBtn =
      target?.closest?.('.cp-cross-curricular-catalog-picker-actions > .cp-btn-secondary') != null;

    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      if (onApplyBtn || onCancelBtn) return;
      e.preventDefault();
      if (navPane === 'contents') {
        moveContentFocus(e.key === 'ArrowDown' ? 1 : -1);
        return;
      }
      const currentIdx = Math.max(
        0,
        topics.findIndex((t) => t.id === selectedTopicId)
      );
      selectTopicByIndex(currentIdx + (e.key === 'ArrowDown' ? 1 : -1));
      return;
    }

    if (e.key === 'ArrowRight') {
      if (onApplyBtn || onCancelBtn) return;
      if (navPane === 'topics') {
        e.preventDefault();
        enterContentsPane();
      }
      return;
    }

    if (e.key === 'ArrowLeft') {
      if (onApplyBtn || onCancelBtn) return;
      if (navPane === 'contents') {
        e.preventDefault();
        setNavPane('topics');
        focusTopicButton(selectedTopicId);
      }
      return;
    }

    if (e.key === 'Enter' && navPane === 'contents' && !onApplyBtn && !onCancelBtn) {
      const focusId = contentFocusId ?? contentsForTopic[0]?.id ?? null;
      if (!focusId) return;
      e.preventDefault();
      e.stopPropagation();
      selectContentAndFocusApply(focusId);
    }
  };

  if (!open) return null;

  return createPortal(
    <div
      className="cp-modal-backdrop cp-cross-curricular-catalog-picker-backdrop"
      role="presentation"
      onClick={onClose}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <div
        ref={dialogRef}
        className="cp-modal cp-cross-curricular-catalog-picker-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cp-cross-curricular-catalog-picker-title"
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={handleDialogKeyDown}
      >
        <h3 id="cp-cross-curricular-catalog-picker-title">범교과 예시문 가져오기</h3>
        <p className="cp-evaluation-plan-level-settings-hint" role="status">
          관리자 범교과 관리에 등록된 주제어별 학습내용을 선택해 반영합니다.
        </p>
        <div className="cp-modal-body cp-cross-curricular-catalog-picker-body">
          {loading ? <p role="status">불러오는 중…</p> : null}
          {error ? <p className="cp-admin-learning-error">{error}</p> : null}
          {!loading && !error && topics.length === 0 ? (
            <p role="status">
              등록된 범교과 예시문이 없습니다. 관리자 범교과 관리에서 먼저 입력해 주세요.
            </p>
          ) : null}
          {!loading && !error && topics.length > 0 ? (
            <div className="cp-cross-curricular-catalog-picker-split">
              <aside
                className={`cp-cross-curricular-catalog-picker-topics${
                  navPane === 'topics' ? ' is-nav-active' : ''
                }`}
                aria-label="주제어 목록"
              >
                <h4 className="cp-cross-curricular-catalog-picker-panel-title">주제어</h4>
                <ul className="cp-cross-curricular-catalog-picker-list" role="listbox">
                  {topics.map((topic) => {
                    const count = contents.filter((c) => c.topic_id === topic.id).length;
                    const active = selectedTopicId === topic.id;
                    return (
                      <li key={topic.id} role="none">
                        <button
                          type="button"
                          ref={(el) => {
                            if (el) topicBtnRefs.current.set(topic.id, el);
                            else topicBtnRefs.current.delete(topic.id);
                          }}
                          className={`cp-cross-curricular-catalog-picker-item${
                            active ? ' is-active' : ''
                          }`}
                          role="option"
                          aria-selected={active}
                          tabIndex={navPane === 'topics' && active ? 0 : -1}
                          onFocus={() => setNavPane('topics')}
                          onClick={() => {
                            setSelectedTopicId(topic.id);
                            setSelectedContentId(null);
                            setNavPane('topics');
                          }}
                        >
                          <span className="cp-cross-curricular-catalog-picker-item-label">
                            {topic.name}
                          </span>
                          <span className="cp-cross-curricular-catalog-picker-item-count">{count}</span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </aside>
              <div
                className={`cp-cross-curricular-catalog-picker-contents${
                  navPane === 'contents' ? ' is-nav-active' : ''
                }`}
                aria-label="학습내용 목록"
              >
                <h4 className="cp-cross-curricular-catalog-picker-panel-title">
                  학습내용
                  {selectedTopic ? ` · ${selectedTopic.name}` : ''}
                </h4>
                {contentsForTopic.length === 0 ? (
                  <p className="cp-dashboard-placeholder" role="status">
                    이 주제어에 등록된 학습내용이 없습니다.
                  </p>
                ) : (
                  <ul className="cp-cross-curricular-catalog-picker-list" role="listbox">
                    {contentsForTopic.map((row) => {
                      const selected = selectedContentId === row.id;
                      const focused = contentFocusId === row.id;
                      return (
                        <li key={row.id} role="none">
                          <button
                            type="button"
                            ref={(el) => {
                              if (el) contentBtnRefs.current.set(row.id, el);
                              else contentBtnRefs.current.delete(row.id);
                            }}
                            className={`cp-cross-curricular-catalog-picker-item cp-cross-curricular-catalog-picker-item--content${
                              selected ? ' is-active' : ''
                            }${focused && navPane === 'contents' ? ' is-focus' : ''}`}
                            role="option"
                            aria-selected={selected}
                            tabIndex={navPane === 'contents' && focused ? 0 : -1}
                            onFocus={() => {
                              setNavPane('contents');
                              setContentFocusId(row.id);
                            }}
                            onClick={() => {
                              setSelectedContentId(row.id);
                              setContentFocusId(row.id);
                              setNavPane('contents');
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                e.stopPropagation();
                                selectContentAndFocusApply(row.id);
                                return;
                              }
                              if (e.key === 'Tab' && !e.shiftKey) {
                                e.preventDefault();
                                e.stopPropagation();
                                applyBtnRef.current?.focus();
                              }
                            }}
                          >
                            <span
                              className={`cp-evaluation-plan-round-check${
                                selected ? ' is-checked' : ''
                              }`}
                              aria-hidden
                            />
                            <span className="cp-cross-curricular-catalog-picker-item-body">
                              {row.body.trim() || '(내용 없음)'}
                            </span>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </div>
          ) : null}
        </div>
        <div className="cp-modal-footer cp-cross-curricular-catalog-picker-actions">
          <button type="button" className="cp-btn-secondary" onClick={onClose} tabIndex={0}>
            취소
          </button>
          <button
            ref={applyBtnRef}
            type="button"
            className="cp-btn-primary cp-cross-curricular-catalog-picker-apply"
            disabled={!canApply}
            tabIndex={0}
            onClick={handleApply}
          >
            반영
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default CrossCurricularCatalogPickerModal;
